/**
 * The Command Sequences drawer: the schema v8 command sequences this monitoring
 * page has heard of since it opened — each running one with every step's state
 * and its time against its timeout, then the last {@link RECENT_RUNS} that
 * finished, with how they ended.
 *
 * Master admins only. A run lays out how an interlock is built (what it waits
 * for, for how long, what it sends afterwards), which is commissioning detail,
 * and the header shows its button only to them; the drawer checks again so a
 * stale open flag never shows it to anyone else.
 *
 * Runs come from `SequenceRunsProvider`, so the drawer must sit inside it —
 * `MonitoringPage` mounts the provider around the whole page.
 */
import React, { useEffect, useState } from "react";
import { Drawer, Empty, Progress, Tag, Tooltip, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";

import type { RootState } from "../../../app/store";
import { SequenceRunStatus } from "../../../flatbuffers/generated/front-end/sequence-run-status";
import type { SequenceStepProgressT } from "../../../flatbuffers/generated/front-end/sequence-step-progress";
import { SequenceStepStatus } from "../../../flatbuffers/generated/front-end/sequence-step-status";
import type { LayoutInterface } from "../../../interfaces/Monitoring.interface";
import { updateOpenCommandSequences } from "../../../redux/monitoring/monitoringSlice";
import isMasterAdmin from "../../../utils/isMasterAdmin";
import { withheldNoticeSubject } from "../withheldCommands/useWithheldCommandNotices";
import { useSequenceRuns } from "./SequenceRunsContext";
import {
  commandedMark,
  formatDuration,
  runElapsedMs,
  runStatusDisplay,
  stepElapsedMs,
  stepLabel,
  stepPercent,
  stepStatusDisplay,
  stepTiming,
} from "./sequenceRunDisplay";
import { RECENT_RUNS, type SequenceRunView } from "./sequenceRuns";

/** How often the times of a running sequence advance on screen. */
const TICK_MS = 1_000;

/** Now, re-read every {@link TICK_MS} while `ticking` — only while a run is in progress. */
function useNow(ticking: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!ticking) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [ticking]);
  return now;
}

/** "SecureCarCall · Car 2 (Bank A), floor 4", from the layouts the grid has mounted. */
function runSubject(run: SequenceRunView, layouts: readonly LayoutInterface[]): string {
  const subject = withheldNoticeSubject(
    layouts,
    run.bankId,
    run.liftId,
    commandedMark(run.commanded, run.firstSeenAtMs),
  );
  const where = subject.floorLabel
    ? `${subject.liftLabel}, floor ${subject.floorLabel}`
    : subject.liftLabel;
  return subject.commandName ? `${subject.commandName} · ${where}` : where;
}

function StepRow({
  step,
  index,
  run,
  nowMs,
}: {
  step: SequenceStepProgressT;
  index: number;
  run: SequenceRunView;
  nowMs: number;
}) {
  const status = stepStatusDisplay(step.status);
  const elapsed = stepElapsedMs(step, run, nowMs);
  const percent = stepPercent(elapsed, step.timeoutMs);
  const showBar =
    run.status === SequenceRunStatus.Running &&
    step.status === SequenceStepStatus.Active &&
    percent !== null;
  return (
    <li className="commandSequences__step" data-testid="command-sequence-step">
      <div className="generalStyles__spaceBetween">
        <Typography.Text>{stepLabel(step, index)}</Typography.Text>
        <Tooltip title={status.meaning}>
          <Tag color={status.color}>{status.label}</Tag>
        </Tooltip>
      </div>
      {step.status !== SequenceStepStatus.Pending && (
        <Typography.Text type="secondary">{stepTiming(elapsed, step.timeoutMs)}</Typography.Text>
      )}
      {showBar && <Progress percent={percent} size="small" showInfo={false} />}
    </li>
  );
}

function RunCard({
  run,
  layouts,
  nowMs,
}: {
  run: SequenceRunView;
  layouts: readonly LayoutInterface[];
  nowMs: number;
}) {
  const status = runStatusDisplay(run.status);
  const name = run.modifierName || "Unnamed sequence";
  const detail = typeof run.progress.detail === "string" ? run.progress.detail : "";
  // A step's position is its identity: VTS reports a run's steps in authored
  // order and never adds, drops or moves one.
  const steps = run.progress.steps.map((step, position) => ({ step, position }));
  return (
    <section
      className="commandSequences__run mb-3"
      aria-label={`${name}: ${status.label}`}
      data-testid="command-sequence-run"
    >
      <div className="generalStyles__spaceBetween">
        <Typography.Text strong>{name}</Typography.Text>
        <Tooltip title={status.meaning}>
          <Tag color={status.color}>{status.label}</Tag>
        </Tooltip>
      </div>
      <Typography.Text type="secondary">
        {runSubject(run, layouts)} · {formatDuration(runElapsedMs(run, nowMs))}
      </Typography.Text>
      {detail !== "" && <Typography.Paragraph type="secondary">{detail}</Typography.Paragraph>}
      <ol className="commandSequences__steps">
        {steps.map(({ step, position }) => (
          <StepRow key={position} step={step} index={position} run={run} nowMs={nowMs} />
        ))}
      </ol>
    </section>
  );
}

export default function CommandSequencesDrawer({
  layouts,
}: {
  /** The grid's layouts, which name each run's car and floor. */
  layouts: readonly LayoutInterface[];
}) {
  const dispatch = useDispatch();
  const open = useSelector((state: RootState) => state.monitoring.openCommandSequences);
  const userLogged = useSelector((state: RootState) => state.home.userLogged);
  const { active, recent } = useSequenceRuns();
  const running = [...active.values()].sort((a, b) => b.firstSeenAtMs - a.firstSeenAtMs);
  const nowMs = useNow(open && running.length > 0);

  if (!isMasterAdmin(userLogged)) return null;

  return (
    <Drawer
      title="Command Sequences"
      placement="right"
      width={520}
      open={open}
      onClose={() => dispatch(updateOpenCommandSequences(false))}
    >
      <Typography.Title level={5}>Running</Typography.Title>
      {running.length === 0 ? (
        <Typography.Paragraph type="secondary">No sequence is running.</Typography.Paragraph>
      ) : (
        running.map((run) => <RunCard key={run.runId} run={run} layouts={layouts} nowMs={nowMs} />)
      )}
      <Typography.Title level={5}>Finished</Typography.Title>
      {recent.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No sequence has finished since this page opened."
        />
      ) : (
        <>
          <Typography.Paragraph type="secondary">
            The last {RECENT_RUNS} sequences to finish, newest first.
          </Typography.Paragraph>
          {recent.map((run) => (
            <RunCard key={run.runId} run={run} layouts={layouts} nowMs={nowMs} />
          ))}
        </>
      )}
    </Drawer>
  );
}
